import { Pencil } from 'lucide-react';
import { useState } from 'react';

import { usePolicy } from '@/react/portainer/policies/queries/usePolicy';
import { AppliedPolicyInfo } from '@/react/portainer/policies/utils/policy-types-metadata';
import { PolicyType } from '@/react/portainer/policies/types';
import { useKubernetesSecurityInitialValues } from '@/react/portainer/policies/components/kubernetes/security/useKubernetesSecurityInitialValues';
import { useKubernetesNetworkSecurityInitialValues } from '@/react/portainer/policies/components/kubernetes/network-security/useKubernetesNetworkSecurityInitialValues';
import { useKubernetesSetupInitialValues } from '@/react/portainer/policies/components/kubernetes/setup/useKubernetesSetupInitialValues';
import { useKubernetesRBACInitialValues } from '@/react/portainer/policies/components/kubernetes/rbac/useKubernetesRBACInitialValues';
import { useKubernetesRegistryInitialValues } from '@/react/portainer/policies/components/kubernetes/registry/useKubernetesRegistryInitialValues';
import { useKubernetesObservabilityInitialValues } from '@/react/portainer/policies/components/kubernetes/observability/useKubernetesObservabilityInitialValues';
import { usePodSecurityStandardsInitialValues } from '@/react/portainer/policies/components/kubernetes/pod-security-standards/usePodSecurityStandardsInitialValues';
import { AllowedSourcesReadOnly } from '@/react/portainer/policies/components/kubernetes/registry/AllowedSourcesReadOnly';
import { getStatusDisplayLabel } from '@/react/portainer/policies/DetailView/AttachmentsDataTables/statusUtils';
import {
  AppliedPolicyStatus,
  AppliedPolicyStatusType,
  EnvironmentId,
} from '@/react/portainer/environments/types';
import { ChangeConfirmationPolicyReadOnly } from '@/react/portainer/policies/components/change-confirmation/ChangeConfirmationPolicyReadOnly';

import { Widget, WidgetBody } from '@@/Widget';
import { Badge, BadgeType } from '@@/Badge';
import { LinkButton } from '@@/LinkButton';
import { CollapseExpandButton } from '@@/CollapseExpandButton';
import { TextTip } from '@@/Tip/TextTip';

import { SecurityPolicyReadOnly } from './components/SecurityPolicyReadOnly';
import { SetupPolicyReadOnly } from './components/SetupPolicyReadOnly';
import { RBACPolicyReadOnly } from './components/RBACPolicyReadOnly';
import { RegistryPolicyReadOnly } from './components/RegistryPolicyReadOnly';
import { ObservabilityK8sPolicyReadOnly } from './components/ObservabilityK8sPolicyReadOnly';
import { PodSecurityStandardsPolicyReadOnly } from './components/PodSecurityStandardsPolicyReadOnly';
import { NetworkSecurityPolicyReadOnly } from './components/NetworkSecurityPolicyReadOnly';

export interface KubernetesPolicySectionProps {
  item: AppliedPolicyInfo;
  canEdit: boolean;
  canExpand?: boolean;
  endpointId?: EnvironmentId;
}

export function KubernetesPolicySection({
  item,
  canEdit,
  canExpand = true,
  endpointId,
}: KubernetesPolicySectionProps) {
  const [isExpanded, setIsExpanded] = useState(canExpand);
  const policyId = item.policy?.policyID;
  const policyQuery = usePolicy(policyId!, {
    enabled: canExpand && !!policyId,
  });

  if (!policyId) {
    return null;
  }

  const policy = policyQuery.data;
  const contentId = `policy-section-${policyId}`;

  return (
    <Widget>
      <div className="widget-header w-full !p-5">
        <div className="widget-title flex w-full items-center gap-2">
          {canExpand && (
            <CollapseExpandButton
              isExpanded={isExpanded}
              onClick={() => setIsExpanded((value) => !value)}
              data-cy={`collapse-policy-${policyId}`}
            />
          )}
          <button
            type="button"
            className="flex flex-1 items-center gap-2 border-0 bg-transparent p-0 text-left focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-5 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => canExpand && setIsExpanded((value) => !value)}
            disabled={!canExpand}
            aria-expanded={isExpanded}
            aria-controls={contentId}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${item.label} ${
              item.policy?.policyName || ''
            } policy section`}
          >
            <span className="flex-1">
              {item.label} - {item.policy?.policyName || 'Unknown'}
            </span>
            {item.policy?.policyStatus?.Status && (
              <Badge type={getStatusBadgeType(item.policy.policyStatus.Status)}>
                {getStatusLabel(item.policy.policyStatus.Status)}
              </Badge>
            )}
          </button>
          {canEdit && (
            <LinkButton
              to="portainer.policy"
              params={{ id: policyId }}
              icon={Pencil}
              size="small"
              color="default"
              data-cy={`edit-policy-button-${policyId}`}
            >
              Edit
            </LinkButton>
          )}
        </div>
      </div>
      <WidgetBody
        loading={policyQuery.isLoading}
        className={isExpanded && canExpand ? undefined : 'hidden'}
      >
        <div id={contentId} aria-hidden={!isExpanded}>
          {policyQuery.isError && (
            <TextTip color="red" className="mb-4">
              Failed to load this policy. Please try again.
            </TextTip>
          )}
          {item.policy?.policyStatus?.Message && (
            <TextTip
              color={getStatusTipColor(item.policy.policyStatus.Status)}
              className="mb-4"
            >
              {item.policy.policyStatus.Message}
            </TextTip>
          )}
          {policy && (
            <PolicyReadOnlyContent
              policy={policy}
              policyType={item.type}
              endpointId={endpointId}
              policyStatus={item.policy?.policyStatus}
            />
          )}
        </div>
      </WidgetBody>
    </Widget>
  );
}

/**
 * Renders the appropriate read-only policy view based on policy type.
 * Each case renders a component that calls only its required hook.
 */
function PolicyReadOnlyContent({
  policy,
  policyType,
  endpointId,
  policyStatus,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
  policyType: PolicyType;
  endpointId?: EnvironmentId;
  policyStatus?: AppliedPolicyStatus;
}) {
  switch (policyType) {
    case 'security-k8s':
      return <SecurityPolicyContent policy={policy} />;
    case 'network-security-k8s':
      return <NetworkSecurityPolicyContent policy={policy} />;
    case 'setup-k8s':
      return <SetupPolicyContent policy={policy} />;
    case 'rbac-k8s':
      return <RBACPolicyContent policy={policy} />;
    case 'registry-k8s':
      return <RegistryPolicyContent policy={policy} />;
    case 'change-confirmation':
      return <ChangeConfirmationPolicyReadOnly policy={policy} />;
    case 'observability-k8s':
      return (
        <ObservabilityPolicyContent
          policy={policy}
          endpointId={endpointId}
          policyStatus={policyStatus}
        />
      );
    case 'pod-security-standards-k8s':
      return <PodSecurityStandardsPolicyContent policy={policy} />;
    default:
      return null;
  }
}

function SecurityPolicyContent({
  policy,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
}) {
  const initialValues = useKubernetesSecurityInitialValues(policy);
  return <SecurityPolicyReadOnly formData={initialValues} />;
}

function NetworkSecurityPolicyContent({
  policy,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
}) {
  const initialValues = useKubernetesNetworkSecurityInitialValues(policy);
  return <NetworkSecurityPolicyReadOnly formData={initialValues} />;
}

function SetupPolicyContent({
  policy,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
}) {
  const initialValues = useKubernetesSetupInitialValues(policy);
  return <SetupPolicyReadOnly formData={initialValues} />;
}

function RBACPolicyContent({
  policy,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
}) {
  const initialValues = useKubernetesRBACInitialValues(policy);
  return <RBACPolicyReadOnly formData={initialValues} />;
}

function RegistryPolicyContent({
  policy,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
}) {
  const initialValues = useKubernetesRegistryInitialValues(policy);
  return (
    <>
      <RegistryPolicyReadOnly
        formData={{ accessRows: initialValues.accessRows || [] }}
      />
      <AllowedSourcesReadOnly
        restrictSources={initialValues.restrictSources ?? false}
        allowedSources={initialValues.allowedSources || []}
      />
    </>
  );
}

function ObservabilityPolicyContent({
  policy,
  endpointId,
  policyStatus,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
  endpointId?: EnvironmentId;
  policyStatus?: AppliedPolicyStatus;
}) {
  const initialValues = useKubernetesObservabilityInitialValues(policy);
  return (
    <ObservabilityK8sPolicyReadOnly
      formData={initialValues}
      endpointId={endpointId}
      policyStatus={policyStatus}
    />
  );
}

function PodSecurityStandardsPolicyContent({
  policy,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
}) {
  const initialValues = usePodSecurityStandardsInitialValues(policy);
  return <PodSecurityStandardsPolicyReadOnly formData={initialValues} />;
}

function getStatusBadgeType(status: AppliedPolicyStatusType): BadgeType {
  if (status === AppliedPolicyStatusType.Applied) return 'success';
  if (status === AppliedPolicyStatusType.Failed) return 'danger';
  if (status === AppliedPolicyStatusType.Warning) return 'warn';
  if (status === AppliedPolicyStatusType.InProgress) return 'info';
  if (status === AppliedPolicyStatusType.NotSupported) return 'muted';
  return 'muted';
}

function getStatusTipColor(
  status: AppliedPolicyStatusType
): 'red' | 'orange' | 'blue' | undefined {
  if (status === AppliedPolicyStatusType.Failed) return 'red';
  if (status === AppliedPolicyStatusType.Warning) return 'orange';
  if (status === AppliedPolicyStatusType.InProgress) return 'blue';
  return undefined;
}

function getStatusLabel(status: AppliedPolicyStatusType) {
  switch (status) {
    case AppliedPolicyStatusType.Applied:
    case AppliedPolicyStatusType.Failed:
    case AppliedPolicyStatusType.Warning:
    case AppliedPolicyStatusType.InProgress:
    case AppliedPolicyStatusType.NotSupported:
      return getStatusDisplayLabel(status);
    default:
      return status;
  }
}
